import { GetServerSideProps, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';

import RenderBlocks from 'components/cms-blocks/render-blocks';
import { TSanityBlock } from 'components/cms-blocks/render-blocks/types';
import Container from 'components/common/container';
import Main from 'components/common/main';
import getMetaTags from 'components/common/meta-tags';
import { filterDataToSingleItem } from 'framework/sanity/filter-data-to-single-item';
import { mapBlocks } from 'framework/sanity/map-blocks';
import { aboutPageQuery } from 'framework/sanity/queries';
import { getClient } from 'framework/sanity/sanity-client';
import { setCacheControlOneHour } from 'utils/cache';

interface IAboutPage {
  _id: string;
  name?: string;
  slug: {
    _type: 'slug';
    current: string;
  };
  blocks: TSanityBlock[];
}

export default function AboutPage({
  data: { page },
}: InferGetServerSidePropsType<typeof getServerSideProps>): React.ReactElement {
  return (
    <>
      <Head>{getMetaTags({ titleFallback: page.name, meta: page.meta })}</Head>
      <Main light>
        <Container noGutter>
          <RenderBlocks blocks={page.blocks || []} />
        </Container>
      </Main>
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  params,
  res,
}) => {
  setCacheControlOneHour(res);

  const data = await getClient().fetch<IAboutPage[]>(aboutPageQuery, {
    slug: params?.slug?.[0] || '/',
  });

  const page = filterDataToSingleItem(data);

  if (!page) {
    return {
      notFound: true,
    };
  }

  const blocks = await mapBlocks(page.blocks);

  return {
    props: {
      data: {
        page: {
          ...page,
          blocks,
        },
      },
    },
  };
};
